import { cva, type VariantProps } from 'class-variance-authority'
import { MoreVertical } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/utils'
import { Menu, MenuItem, type MenuItemProps, MenuSeparator, MenuTrigger } from './menu'

const kebabTriggerVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center rounded-full transition-colors',
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300',
    'disabled:pointer-events-none disabled:opacity-50',
    // Every size paints smaller than it responds to: the pseudo-element below tops the hit
    // area up to 44px (Apple HIG / WCAG). These buttons are often the only route to a
    // destructive action, and get used in a kitchen.
    "before:absolute before:content-['']"
  ],
  {
    variants: {
      variant: {
        /** Bare — for a card or row that already reads as a surface. */
        ghost:
          'text-muted-foreground hover:bg-block-bg hover:text-foreground data-[state=open]:bg-block-bg data-[state=open]:text-foreground',
        /** Bordered — for a plain background that gives the button no edge of its own. */
        outline:
          'border border-border bg-card text-muted-foreground hover:border-brand-green-border hover:text-foreground data-[state=open]:border-brand-green-border data-[state=open]:text-foreground'
      },
      size: {
        sm: 'size-[34px] before:-inset-[5px]',
        md: 'size-10 before:-inset-[2px]',
        lg: 'size-11 before:inset-0'
      }
    },
    defaultVariants: { variant: 'ghost', size: 'md' }
  }
)

export interface KebabMenuProps extends VariantProps<typeof kebabTriggerVariants> {
  /**
   * Accessible name for the trigger — required, and it should name the *menu*, not any one
   * item in it ("Plan options", not "End plan early"). Already-translated; this package
   * stays i18n-agnostic.
   */
  label: string
  /** `KebabMenuItem`s, `KebabMenuSeparator`s, and whatever conditionals you need between them. */
  children: React.ReactNode
  /** Override the default vertical-ellipsis glyph. */
  icon?: React.ReactNode
  align?: 'start' | 'center' | 'end'
  /**
   * Set when the menu sits inside a clickable surface (a row that expands, a card that
   * opens) — clicks on the trigger and the panel then stay put instead of also firing the
   * surface behind them.
   */
  stopPropagation?: boolean
  /** Passed to the menu panel — e.g. to redirect focus somewhere other than the trigger on close. */
  onCloseAutoFocus?: (event: Event) => void
  /** Extra classes for the trigger. */
  className?: string
  /** Extra classes for the menu panel. */
  contentClassName?: string
}

// The app's overflow ("kebab") menu: a `Menu` with the round ellipsis trigger.
//
// The trigger lives here and the behaviour lives in `Menu` — this file owns nothing but how the
// button LOOKS. That split is why the kebab and any other menu can't drift apart on open/close
// or accessibility: neither of them implements it.
//
// Both halves of the *appearance* stayed together on purpose. The trigger had drifted across
// call sites — three sizes, two icons, two of three missing a focus ring, one labelled after a
// single destructive action rather than the menu — and the panel had drifted the same way, each
// site re-specifying padding, radius and a destructive tone slightly differently. Callers supply
// only what's actually theirs: the items.
export function KebabMenu({
  label,
  children,
  icon,
  variant,
  size,
  align = 'end',
  stopPropagation = false,
  onCloseAutoFocus,
  className,
  contentClassName
}: KebabMenuProps) {
  return (
    <Menu
      align={align}
      stopPropagation={stopPropagation}
      onCloseAutoFocus={onCloseAutoFocus}
      contentClassName={contentClassName}
      trigger={
        <MenuTrigger aria-label={label} className={cn(kebabTriggerVariants({ variant, size, className }))}>
          {icon ?? <MoreVertical size={16} aria-hidden="true" />}
        </MenuTrigger>
      }>
      {children}
    </Menu>
  )
}

// The rows are the shared `Menu` ones — aliased so existing call sites keep reading as a set.
export type KebabMenuItemProps = MenuItemProps
const KebabMenuItem = MenuItem
const KebabMenuSeparator = MenuSeparator

export { KebabMenuItem, KebabMenuSeparator, kebabTriggerVariants }
